import { computed, Injectable, signal } from '@angular/core';
import { Observable, switchMap, tap, timer } from 'rxjs';
import { ApiService } from '../api.service';
import { LuckyCells, LuckyCellsGame, LuckyCellsState, WALLET_CODE } from '@libs/interfaces';
import { Matrix } from '@libs/helper/layout/luckt-cells.helper';
import { WalletService } from '../wallet/wallet.service';
import { orderBy, uniqBy } from 'lodash';
@Injectable({ providedIn: 'root' })
export class LuckyCellsService {
  // Time until next game can be played
  readonly MILISECONDS_TILL_NEXT_DRAW = 6 * 60 * 60 * 1000; // 6 hours in milliseconds

  // Game state management
  state = signal<LuckyCellsState>(LuckyCellsState.WAITING);

  // Matrix configuration
  readonly num_rows = 4;
  readonly num_cols = 4;
  readonly matrix = signal<LuckyCells[]>([]);

  // Game history tracking
  lastLuckyCellGame = signal<LuckyCellsGame | null>(null); // Most recent completed game
  recentLuckyCellGame = signal<LuckyCellsGame | null>(null); // Currently active game
  remainingTimeToPlay = signal<number | null>(null); // Countdown until next game allowed
  enabledPlay = computed(() => this.remainingTimeToPlay() === 0); // Whether player can start new game
  luckyCellGames = signal<LuckyCellsGame[]>([]); // List of all games played

  constructor(
    private readonly api: ApiService,
    private readonly walletService: WalletService,
  ) {}

  init() {
    this.state.set(LuckyCellsState.WAITING);
    this.remainingTimeToPlay.set(null);
    this.recentLuckyCellGame.set(null);

    return this.getLastGame().pipe(
      tap(() => this.initializeMatrix()),
      switchMap(() => this.caclRemaining()),
    );
  }
  getLastGame() {
    return this.api
      .get('lucky-cells/last-game')
      .pipe(tap((game: LuckyCellsGame) => this.lastLuckyCellGame.set(game || null)));
  }
  /**
   * Creates a new game for the current user
   */
  addLuckyCellGame(): Observable<LuckyCellsGame> {
    return this.api
      .post('lucky-cells/add-game', { wallet: this.walletService.wallet().code })
      .pipe(tap(game => this.recentLuckyCellGame.set(game)));
  }

  /**
   * Gets paginated list of games for a wallet
   */
  getLuckyCellGames(wallet: WALLET_CODE, offset: number, limit: number): Promise<LuckyCellsGame[]> {
    return this.api
      .get(`lucky-cells/games-list/${wallet}/${offset}/${limit}`)
      .pipe(
        tap(games =>
          this.luckyCellGames.update(_games => orderBy(uniqBy([...games, ..._games], '_id'), ['createdAt'], ['desc'])),
        ),
      )
      .toPromise();
  }

  /**
   * Resets game state after completion
   */
  resetLuckyCellGame() {
    if (this.recentLuckyCellGame()) {
      this.lastLuckyCellGame.update(() => this.recentLuckyCellGame());
      this.recentLuckyCellGame.set(null);
      this.state.set(LuckyCellsState.WAITING);
      this.showMatrix();
    }
  }

  /**
   * Selects a random cell based on probability weights
   */
  getRandomCellUsingProbabilities(): LuckyCells {
    // Use crypto.getRandomValues() for better randomization
    const randomBuffer = new Uint8Array(1);
    crypto.getRandomValues(randomBuffer);
    const random = randomBuffer[0] / 255; // Normalize to 0-1 range

    const cellsArray = this.matrix();
    const totalProbability = cellsArray.reduce((sum, cell) => sum + cell.probabilities, 0);
    const randomValue = random * totalProbability;
    let cumulativeProbability = 0;

    for (const cell of cellsArray) {
      cumulativeProbability += cell.probabilities;
      if (randomValue <= cumulativeProbability) {
        return cell;
      }
    }

    // Fallback in case of rounding errors
    return cellsArray[cellsArray.length - 1];
  }

  /**
   * Randomly shows/hides cells during gameplay animation
   */
  randomizeMatrix() {
    const lastShowIndex = this.getLastShowIndex();
    if (lastShowIndex > -1) {
      this.matrix.update(cells =>
        cells.map((cell, index) => (index === lastShowIndex ? { ...cell, state: 'hide' } : cell)),
      );
    }
    let randomIndex: number;
    do {
      const randomBuffer = new Uint8Array(1);
      crypto.getRandomValues(randomBuffer);
      randomIndex = Math.floor((randomBuffer[0] / 255) * this.matrix().length);
    } while (randomIndex === lastShowIndex);
    this.matrix.update(cells =>
      cells.map((cell, index) => (index === randomIndex ? { ...cell, state: 'show' } : cell)),
    );
  }

  /**
   * Hides all cells in matrix
   */
  hideMatrix() {
    this.matrix.update(cells => cells.map(cell => ({ ...cell, state: 'hide' })));
  }

  /**
   * Shows all cells in matrix
   */
  showMatrix() {
    this.matrix.update(cells => cells.map(cell => ({ ...cell, state: 'show' })));
  }

  /**
   * Gets index of last visible cell
   */
  getLastShowIndex() {
    return this.matrix().findIndex(cell => cell.state === 'show');
  }

  /**
   * Shows only the winning cell at given index
   */
  showLuckyCell(index: number) {
    this.matrix.update(cells =>
      cells.map((cell, i) => (i === index ? { ...cell, state: 'show' } : { ...cell, state: 'hide' })),
    );
  }
  /**
   * Sets up initial matrix state
   */
  private initializeMatrix() {
    this.matrix.set(Matrix.map(cell => ({ ...cell, state: 'show' })));
  }
  /**
   * Calculates remaining time until next game can be played
   * Updates every second via timer
   */
  private caclRemaining() {
    return timer(0, 1000).pipe(
      tap(() => {
        const lastGame = this.lastLuckyCellGame();
        if (!lastGame) {
          if (this.state() === LuckyCellsState.WAITING) {
            this.remainingTimeToPlay.set(0);
            this.state.set(LuckyCellsState.READY);
          }
          return;
        }

        const expirationTime = lastGame.createdAt + this.MILISECONDS_TILL_NEXT_DRAW;
        const remainingTime = Math.max(0, expirationTime - Date.now());
        this.remainingTimeToPlay.set(remainingTime);
        if (this.enabledPlay() && this.state() === LuckyCellsState.WAITING) {
          this.state.set(LuckyCellsState.READY);
        }
      }),
    );
  }
}
